package com.siriusxm.pia.operations.dashboard

import androidx.compose.runtime.Composable
import com.siriusxm.pia.SXMUI
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Direction of a [StatCard] trend indicator.
 *
 * [direction] drives the arrow glyph and colour; [positiveIsGood] lets callers decide whether
 * an upward trend is desirable (e.g. active producers) or not (e.g. error rate), since the
 * "good" colour should follow business meaning rather than arrow direction.
 */
enum class TrendDirection { Up, Down, Flat }

/**
 * A trend annotation for a [StatCard]: an arrow, an optional delta label (e.g. "+12%"), and a
 * colour derived from whether the movement is good or bad.
 *
 * @param direction which way the metric moved.
 * @param label optional text shown next to the arrow, e.g. a percentage or absolute delta.
 * @param positiveIsGood when true (default), [TrendDirection.Up] is coloured as "good"
 *   (green) and [TrendDirection.Down] as "bad" (red); set false to invert (e.g. for error
 *   counts where down is good). [TrendDirection.Flat] is always neutral.
 */
data class Trend(
    val direction: TrendDirection,
    val label: String? = null,
    val positiveIsGood: Boolean = true
)

/** Stylesheet for the [StatCard] KPI widget. */
object StatCardStyles : StyleSheet() {
    /** Center the value/label stack and let it fill the widget cell height. */
    val statCard by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        textAlign("center")
        gap(4.px)
        height(100.percent)
    }

    val statValue by style {
        fontSize(2.4.cssRem)
        fontWeight("bold")
        lineHeight(1.1.em)
        color(SXMUI.textColor.value())
    }

    val statLabel by style {
        fontSize(SXMUI.smallFontSize.value())
        color(SXMUI.subtleTextColor.value())
    }

    val statTrend by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(4.px)
        fontSize(0.85.cssRem)
        marginTop(2.px)
    }
}

/**
 * A single KPI tile: a large [value] with a descriptive [label] beneath it, and an optional
 * [trend] indicator (arrow + delta) coloured green/red/neutral based on the trend's meaning.
 *
 * Intended to be dropped into a [Widget]. Typically the widget is given no title so the value
 * itself is the focal point:
 *
 * ```
 * Widget(span = 3, minWidth = 200) {
 *     StatCard(label = "Active Producers", value = "12", trend = Trend(TrendDirection.Up, "+2"))
 * }
 * ```
 *
 * @param label the metric name shown under the value.
 * @param value the metric value, pre-formatted as a display string.
 * @param trend optional movement indicator.
 */
@Composable
fun StatCard(
    label: String,
    value: String,
    trend: Trend? = null
) {
    Div({ classes(StatCardStyles.statCard) }) {
        Div({ classes(StatCardStyles.statValue) }) { Text(value) }
        Div({ classes(StatCardStyles.statLabel) }) { Text(label) }
        trend?.let { TrendIndicator(it) }
    }
}

/**
 * Renders a [Trend] as a coloured arrow glyph plus optional delta label. Up/down are coloured
 * by [Trend.positiveIsGood]; flat is neutral (subtle text colour).
 */
@Composable
private fun TrendIndicator(trend: Trend) {
    val glyph = when (trend.direction) {
        TrendDirection.Up -> "▲"
        TrendDirection.Down -> "▼"
        TrendDirection.Flat -> "—"
    }
    val color = when (trend.direction) {
        TrendDirection.Flat -> SXMUI.subtleTextColor.value()
        TrendDirection.Up -> if (trend.positiveIsGood) SXMUI.goColor.value() else SXMUI.stopColor.value()
        TrendDirection.Down -> if (trend.positiveIsGood) SXMUI.stopColor.value() else SXMUI.goColor.value()
    }
    Div({
        classes(StatCardStyles.statTrend)
        style { color(color) }
    }) {
        Span { Text(glyph) }
        trend.label?.let { Span { Text(it) } }
    }
}
